"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type { Collection } from "@/features/collections/types";
import { SecGlyph } from "@/features/home-v2/ornament";
import { toCollectionView, useCollectionToggles } from "./collectionView";
import { ChainChip, CollectionCreator, CollectionLogo, CollectionName, CollectionToggles } from "./CollectionParts";

/** One entry of the podium: the leader large, the next two as side plates. */
function Entry(t0) {
  const $ = _c(70);
  const {
    collection,
    rank,
    lang,
    lead
  } = t0;
  let t1;
  if ($[0] !== collection || $[1] !== lang) {
    t1 = toCollectionView(collection, lang);
    $[0] = collection;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const v = t1;
  const t = useCollectionToggles(collection, lang);
  let Arrow;
  let num;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== lang || $[4] !== lead || $[5] !== rank || $[6] !== v.banner || $[7] !== v.href || $[8] !== v.name) {
    num = new Intl.NumberFormat(lang === "fa" ? "fa-IR" : "en-US");
    Arrow = lang === "fa" ? ArrowLeft : ArrowRight;
    t5 = "v2-clx-spot";
    t6 = lead || undefined;
    if ($[19] !== v.href || $[20] !== v.name) {
      t7 = <Link className="v2-clx-card-link v2-focus" href={v.href} aria-label={v.name} />;
      $[19] = v.href;
      $[20] = v.name;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    const t10 = lead ? "high" : undefined;
    const t11 = lead ? "(max-width: 1023px) 100vw, 60vw" : "(max-width: 1023px) 100vw, 30vw";
    let t12;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        objectFit: "cover"
      };
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    if ($[23] !== lead || $[24] !== t10 || $[25] !== t11 || $[26] !== v.banner) {
      t8 = <span className="v2-clx-spot-art"><Image src={v.banner} alt="" fill={true} preload={lead} fetchPriority={t10} sizes={t11} style={t12} /></span>;
      $[23] = lead;
      $[24] = t10;
      $[25] = t11;
      $[26] = v.banner;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <span className="v2-clx-spot-shade" aria-hidden={true} />;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    t2 = "v2-clx-spot-no";
    t3 = true;
    t4 = num.format(rank);
    $[3] = lang;
    $[4] = lead;
    $[5] = rank;
    $[6] = v.banner;
    $[7] = v.href;
    $[8] = v.name;
    $[9] = Arrow;
    $[10] = num;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    Arrow = $[9];
    num = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t10;
  if ($[29] !== t2 || $[30] !== t3 || $[31] !== t4) {
    t10 = <span className={t2} aria-hidden={t3}>{t4}</span>;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== num || $[34] !== t) {
    t11 = <CollectionToggles t={t} num={num} />;
    $[33] = num;
    $[34] = t;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const t12 = lead ? 64 : 46;
  let t13;
  if ($[36] !== t12 || $[37] !== v) {
    t13 = <CollectionLogo v={v} size={t12} className="v2-clx-spot-logo" />;
    $[36] = t12;
    $[37] = v;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== lang || $[40] !== v) {
    t14 = <CollectionName v={v} lang={lang} />;
    $[39] = lang;
    $[40] = v;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== lang || $[43] !== v) {
    t15 = <CollectionCreator v={v} lang={lang} />;
    $[42] = lang;
    $[43] = v;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== v) {
    t16 = <ChainChip v={v} />;
    $[45] = v;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t15 || $[48] !== t16) {
    t17 = <div className="v2-clx-spot-meta">{t15}{t16}</div>;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t14 || $[51] !== t17) {
    t18 = <div className="v2-clx-spot-text">{t14}{t17}</div>;
    $[50] = t14;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== Arrow || $[54] !== lang || $[55] !== lead) {
    t19 = lead ? <span className="v2-clx-spot-go" aria-hidden={true}>{lang === "fa" ? "\u062F\u06CC\u062F\u0646 \u06A9\u0627\u0644\u06A9\u0634\u0646" : "View collection"}<Arrow /></span> : null;
    $[53] = Arrow;
    $[54] = lang;
    $[55] = lead;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t13 || $[58] !== t18 || $[59] !== t19) {
    t20 = <div className="v2-clx-spot-body">{t13}{t18}{t19}</div>;
    $[57] = t13;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== t10 || $[62] !== t11 || $[63] !== t20 || $[64] !== t5 || $[65] !== t6 || $[66] !== t7 || $[67] !== t8 || $[68] !== t9) {
    t21 = <article className={t5} data-lead={t6}>{t7}{t8}{t9}{t10}{t11}{t20}</article>;
    $[61] = t10;
    $[62] = t11;
    $[63] = t20;
    $[64] = t5;
    $[65] = t6;
    $[66] = t7;
    $[67] = t8;
    $[68] = t9;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  return t21;
}

/**
 * THE PODIUM — the first three of the Top tab, lifted out of the grid on the
 * first page with no filters. It reads the same query the list reads (no new
 * request), and the list below it continues from rank four, so nothing is
 * shown twice and nothing is dropped.
 */
export default function CollectionSpotlight(t0) {
  const $ = _c(21);
  const {
    items,
    lang
  } = t0;
  const [first, second, third] = items;
  if (!first || !second || !third) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SecGlyph className="v2-sec-glyph" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = lang === "fa" ? "\u0633\u0647 \u06A9\u0627\u0644\u06A9\u0634\u0646 \u0628\u0631\u062A\u0631" : "The top three";
  let t3;
  if ($[1] !== t2) {
    t3 = <div className="v2-sec-head"><h2 className="v2-sec-title" id="v2-clx-podium-title">{t1}<span>{t2}</span></h2></div>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== first || $[4] !== lang) {
    t4 = <Entry collection={first} rank={1} lang={lang} lead={true} />;
    $[3] = first;
    $[4] = lang;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== lang || $[7] !== second) {
    t5 = <Entry collection={second} rank={2} lang={lang} lead={false} />;
    $[6] = lang;
    $[7] = second;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== lang || $[10] !== third) {
    t6 = <Entry collection={third} rank={3} lang={lang} lead={false} />;
    $[9] = lang;
    $[10] = third;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="v2-clx-podium-side">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t7) {
    t8 = <div className="v2-clx-podium-grid">{t4}{t7}</div>;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t3 || $[19] !== t8) {
    t9 = <section className="v2-clx-podium" aria-labelledby="v2-clx-podium-title">{t3}{t8}</section>;
    $[18] = t3;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}